import { UserRoleEnum, tryNormalizeRole } from "@/types/role.type";
import type { MenuItem } from "../types/Sidebar.type";
import { RoleRepository } from "./RoleRepository";
import {
  BarChart3,
  Users,
  Building2,
  FileText,
  Settings,
  Calendar,
  MessageSquare,
  Download,
  Monitor,
  AlertTriangle,
  Search,
  Layers,
  Zap,
  PieChart,
  Beaker,
  ClipboardList,
  BookOpen,
  TrendingUp,
  Package,
  LayoutDashboard,
  FolderTree,
  Database,
  CheckCircle,
  Activity,
  History,
  FolderOpen,
  CheckSquare,
  FormInput,
  GitBranch,
  Eye,
  Shield,
  Grid3x3,
  ListOrdered,
  FileCode,
  FileSearch,
  Table,
  CheckCircle2,
  Lock,
  Heart,
  Snowflake,
  BadgeCheck,
  UsersRound,
  Folder,
  GitPullRequest,
  ClipboardCheck,
  Archive,
  UserPlus,
  CalendarDays,
  ListChecks,
  Construction,
  GitMerge,
  Workflow,
  FileUp,
  Wallet,
  Receipt,
  Send,
  TriangleAlert,
  BarChart,
  Edit3,
  FileCog,
  FilePlus,
  FileOutput,
  FileSpreadsheet,
  Flag,
  FlaskConical,
  HelpCircle,
  MapPin,
  PackageCheck,
  EyeOff,
  Shuffle,
  Truck,
  Code,
  Clock,
  Search as SearchIcon,
  Plug,
} from "lucide-react";
type MenuIcon = MenuItem["icon"];

interface RawMenuItem {
  id: string;
  name: string;
  icon: string;
  path: string;
  active: boolean;
  type?: "group" | "item";
  readOnly?: boolean;
  children?: RawMenuItem[];
}

/**
 * Icon map to convert icon names from JSON to actual icon components
 */
const iconMap: Record<string, MenuIcon> = {
  BarChart3,
  Users,
  Building2,
  FileText,
  Settings,
  Calendar,
  MessageSquare,
  Download,
  Monitor,
  AlertTriangle,
  Search,
  Layers,
  Zap,
  PieChart,
  Beaker,
  ClipboardList,
  BookOpen,
  TrendingUp,
  Package,
  LayoutDashboard,
  FolderTree,
  Database,
  CheckCircle,
  Activity,
  History,
  FolderOpen,
  CheckSquare,
  FormInput,
  GitBranch,
  Eye,
  Shield,
  Grid3x3,
  ListOrdered,
  FileCode,
  FileSearch,
  Table,
  CheckCircle2,
  Lock,
  Heart,
  Snowflake,
  BadgeCheck,
  UsersRound,
  Folder,
  GitPullRequest,
  ClipboardCheck,
  Archive,
  UserPlus,
  CalendarDays,
  ListChecks,
  Construction,
  GitMerge,
  Workflow,
  FileUp,
  Wallet,
  Receipt,
  Send,
  TriangleAlert,
  BarChart,
  Edit3,
  FileCog,
  FilePlus,
  FileOutput,
  FileSpreadsheet,
  Flag,
  FlaskConical,
  HelpCircle,
  MapPin,
  PackageCheck,
  EyeOff,
  Shuffle,
  Truck,
  Code,
  Clock,
  SearchIcon,
  Plug,
};
/**
 * Convert JSON menu items to MenuItem objects with icon components
 */
function convertMenuItems(items: RawMenuItem[]): MenuItem[] {
  return items.map((item) => ({
    ...item,
    icon: iconMap[item.icon] ?? BarChart3,
    children: item.children ? convertMenuItems(item.children) : undefined,
  }));
}
function forceCraStudyMilestonesWritable(items: MenuItem[]): MenuItem[] {
  return items.map((item) => {
    const normalizedPath = (item.path || "").replace(
      /^\/(tenant|cra|pm|sponsor|sc|pi|site-admin|ctms)\//,
      "/",
    );
    const patched: MenuItem = { ...item };
    if (normalizedPath === "/study-milestones") {
      patched.readOnly = false;
    }
    if (item.children?.length) {
      patched.children = forceCraStudyMilestonesWritable(item.children);
    }
    return patched;
  });
}
/**
 * Inject "Monitor Tracker" into CTMS group for Data Manager and Principal Investigator
 * so they see it in the sidebar (read-only), same as PM, even when API menu config doesn't include it.
 */
function injectMonitorTrackerForReadOnlyRoles(items: MenuItem[]): MenuItem[] {
  const hasMonitorTracker = (children: MenuItem[] | undefined): boolean =>
    !!children?.some((c) => c.id === "monitoring-tracker");
  return items.map((item) => {
    const isCtmsGroup =
      item.id === "ctms" || String(item.name).toLowerCase() === "ctms";
    if (!isCtmsGroup || hasMonitorTracker(item.children)) {
      return item;
    }
    const newChild: MenuItem = {
      id: "monitor-tracker",
      name: "Monitor Tracker",
      path: "/monitor-tracker",
      active: true,
      readOnly: true,
      icon: iconMap["ClipboardCheck"] ?? ClipboardCheck,
    };
    return {
      ...item,
      children: [...(item.children ?? []), newChild],
    };
  });
}
let roleMenuConfigs: Record<UserRoleEnum, MenuItem[]> | null = null;
/** Menus for API role keys that are not yet mapped to UserRoleEnum (e.g. Safety *). */
let extraRoleMenus: Record<string, MenuItem[]> | null = null;
const roleRepository = new RoleRepository();

/** Prefer these API keys when several roles alias to the same UserRoleEnum. */
const PREFERRED_MENU_API_KEY: Partial<Record<UserRoleEnum, string>> = {
  [UserRoleEnum.DATA_MANAGER]: "Data Manager",
  [UserRoleEnum.TENANT]: "Tenant Admin",
  [UserRoleEnum.FM]: "Finance Manager",
  [UserRoleEnum.STUDY_COORDINATOR]: "Study Coordinator",
};

const canonicalizeMenuRoleKey = (role: string): string =>
  role.toLowerCase().replace(/[\s/\-_]+/g, "");

/**
 * Clear cached menu configurations (useful for development/testing)
 */
export function clearMenuCache() {
  roleMenuConfigs = null;
  extraRoleMenus = null;
}
/**
 * Load menu configurations from bundled local JSON as fallback
 */
export async function loadLocalMenuConfigs(): Promise<Record<
  string,
  RawMenuItem[]
> | null> {
  try {
    const local = await import("../../menu-configurations.json");
    const data =
      (local as { default?: Record<string, RawMenuItem[]> }).default ?? local;
    return data as Record<string, RawMenuItem[]>;
  } catch {
    return null;
  }
}
/**
 * Get cached menu configurations or fetch from API
 *
 * Flow:
 * 1. Returns cached data if available
 * 2. Fetches from API endpoint: GET /roles/menu-config
 * 3. If API fails, falls back to local menu-configurations.json
 * 4. Caches and returns the data
 *
 * Expected API Response Format:
 * {
 *   success: true,
 *   data: {
 *     success: true,
 *     data: {
 *       "Tenant Admin": [...],
 *       "Principal Investigator": [...],
 *       ...
 *     }
 *   }
 * }
 */
async function getRoleMenuConfigs(): Promise<Record<UserRoleEnum, MenuItem[]>> {
  if (roleMenuConfigs) {
    return roleMenuConfigs;
  }
  let menuData: Record<string, RawMenuItem[]> | null = null;
  try {
    const response = await roleRepository.getMenuConfigurations();
    // Check if API call was successful
    if (
      !response.success ||
      !response.data ||
      Object.keys(response.data).length === 0
    ) {
      throw new Error("API returned empty or unsuccessful menu data");
    }
    // Handle nested data structure: response.data.data
    const rawMenuData = response.data?.data ?? response.data;
    // Ensure it's a Record, not an array
    if (Array.isArray(rawMenuData)) {
      throw new Error("API returned array instead of role-based menu object");
    }
    menuData = rawMenuData as Record<string, RawMenuItem[]>;
    if (!menuData || Object.keys(menuData).length === 0) {
      throw new Error("API returned empty menu data after processing");
    }
  } catch {
    // API unavailable — fall back to public menu-configurations.json so sidebar still works
    menuData = await loadLocalMenuConfigs();
    if (!menuData || Object.keys(menuData).length === 0) {
      return {} as Record<UserRoleEnum, MenuItem[]>;
    }
  }
  if (menuData && Object.keys(menuData).length > 0) {
    const configs: Record<string, MenuItem[]> = {};
    const extras: Record<string, MenuItem[]> = {};
    const sourceKeys: Partial<Record<UserRoleEnum, string>> = {};
    // Normalize menu data keys to handle typo variants (e.g., "Study Cordinator" -> "Study Coordinator")
    const normalizedMenuData: Record<string, RawMenuItem[]> = {};
    for (const [key, value] of Object.entries(menuData)) {
      // Normalize "Cordinator" to "Coordinator" in keys
      const normalizedKey = key.replace(/Cordinator/gi, "Coordinator");
      normalizedMenuData[normalizedKey] = value;
    }
    // Map API role names to UserRoleEnum via shared tryNormalizeRole
    for (const [apiRoleName, items] of Object.entries(normalizedMenuData)) {
      let converted = convertMenuItems(items);
      const roleEnum = tryNormalizeRole(apiRoleName);
      if (!roleEnum) {
        // Keep Safety * (and other future API-only roles) usable without
        // collapsing them onto Tenant Admin via normalizeRole().
        extras[canonicalizeMenuRoleKey(apiRoleName)] = converted;
        continue;
      }
      converted =
        roleEnum === UserRoleEnum.CRA
          ? forceCraStudyMilestonesWritable(converted)
          : converted;
      // Ensure DM and PI see Monitor Tracker in sidebar (read-only), same as PM
      if (
        roleEnum === UserRoleEnum.DATA_MANAGER ||
        roleEnum === UserRoleEnum.PRINCIPAL_INVESTIGATOR
      ) {
        converted = injectMonitorTrackerForReadOnlyRoles(converted);
      }

      const preferred = PREFERRED_MENU_API_KEY[roleEnum];
      const existingSource = sourceKeys[roleEnum];
      if (
        existingSource &&
        preferred &&
        existingSource === preferred &&
        apiRoleName !== preferred
      ) {
        // Keep canonical "Data Manager" / "Tenant Admin" menu over aliases
        continue;
      }

      configs[roleEnum] = converted;
      sourceKeys[roleEnum] = apiRoleName;
    }
    roleMenuConfigs = configs;
    extraRoleMenus = extras;
    return roleMenuConfigs;
  }
  return {} as Record<UserRoleEnum, MenuItem[]>;
}

async function resolveMenuItemsForRole(userRole: string): Promise<MenuItem[]> {
  const configs = await getRoleMenuConfigs();
  const mapped = tryNormalizeRole(userRole);
  if (mapped) {
    return configs[mapped] || [];
  }
  // Exact API role menus that are not in UserRoleEnum yet (Safety *, etc.)
  const extras = extraRoleMenus ?? {};
  const byExactName = extras[canonicalizeMenuRoleKey(userRole)];
  if (byExactName?.length) {
    return byExactName;
  }
  // Preserve prior behavior: completely unknown roles fall back to Tenant Admin
  return configs[UserRoleEnum.TENANT] || [];
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class -- static menu repository
export class MenuRepository {
  static async fetchMenuItems(userRole?: string): Promise<MenuItem[]> {
    if (!userRole) {
      return [];
    }
    return resolveMenuItemsForRole(userRole);
  }
  static async getMenuByRole(userRole: string): Promise<MenuItem[]> {
    return resolveMenuItemsForRole(userRole);
  }
  static async findMenuItemByPath(
    path: string,
    userRole: string,
  ): Promise<MenuItem | null> {
    const menuItems = await resolveMenuItemsForRole(userRole);
    return this.searchMenuByPath(path, menuItems);
  }
  static async isPathAccessible(
    path: string,
    userRole: string,
  ): Promise<boolean> {
    const menuItems = await resolveMenuItemsForRole(userRole);
    const menuItem = this.searchMenuByPath(path, menuItems);
    return menuItem ? menuItem.active : false;
  }
  /**
   * Recursively search for menu item by path
   */
  private static searchMenuByPath(
    path: string,
    items: MenuItem[],
  ): MenuItem | null {
    for (const item of items) {
      if (item.path === path) {
        return item;
      }
      if (item.children) {
        const found = this.searchMenuByPath(path, item.children);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }
}
